import { useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { getAdminEmailErrorMessage, getAdminEmailMailboxes } from "../services/admin-email.service";
import { adminEmailMailboxRefreshAtom, adminEmailMailboxStatusAtom } from "../state/admin-email.atoms";
import { ADMIN_EMAIL_COUNTS_REFRESH_MS } from "../utils/admin-email.constants";

// Own one polling loop in the admin sidebar so navigating between pages does not multiply requests.
// Counts clear when the authenticated account changes, and a failed refresh never looks like zero unread mail.
export const useAdminEmailMailboxCounts = (enabled: boolean, accountId: string | undefined) => {
  const refresh = useAtomValue(adminEmailMailboxRefreshAtom);
  const setStatus = useSetAtom(adminEmailMailboxStatusAtom);

  useEffect(() => {
    setStatus({ accountId: accountId ?? null, response: null, loading: false, error: null });

    return () => {
      setStatus({ accountId: null, response: null, loading: false, error: null });
    };
  }, [enabled, accountId, setStatus]);

  useEffect(() => {
    if (!enabled || !accountId) {
      return;
    }

    const controller = new AbortController();
    let timer: number | undefined;

    const loadMailboxCounts = async () => {
      setStatus((current) => ({ ...current, loading: true }));

      try {
        const response = await getAdminEmailMailboxes(controller.signal);

        if (!controller.signal.aborted) {
          setStatus({ accountId, response, loading: false, error: null });
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setStatus((current) => ({ ...current, loading: false, error: getAdminEmailErrorMessage(error) }));
        }
      } finally {
        if (!controller.signal.aborted) {
          timer = window.setTimeout(loadMailboxCounts, ADMIN_EMAIL_COUNTS_REFRESH_MS);
        }
      }
    };

    void loadMailboxCounts();

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [enabled, accountId, refresh, setStatus]);
};
